import React, { useState, useEffect, useCallback } from 'react';
import { Anchor, Plus, Edit2, Trash2, Check, X, Search, AlertCircle } from 'lucide-react';
import { api } from '../config/api';
import { catalogService } from '../services/catalogService';
import { parseErrorMessage } from '../utils/helpers';

const ENDPOINT = '/pricing/ports/';
const toArr = (data) => (Array.isArray(data) ? data : data?.results || data?.items || data?.data || []);

export default function PortsManager() {
  const [ports, setPorts]             = useState([]);
  const [countries, setCountries]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [searchTerm, setSearchTerm]   = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [editingPort, setEditingPort] = useState(null);
  const [formName, setFormName]       = useState('');
  const [formCountryId, setFormCountryId] = useState('');
  const [saving, setSaving]           = useState(false);
  const [formError, setFormError]     = useState('');
  const [deleteId, setDeleteId]       = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(ENDPOINT + (searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''));
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      setPorts(list);
    } catch {
      setPorts([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    catalogService.countries.getAll({ active_only: true })
      .then((data) => setCountries(toArr(data).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'))))
      .catch(() => setCountries([]));
  }, []);

  const openCreate = () => {
    setEditingPort(null);
    setFormName('');
    setFormCountryId('');
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (port) => {
    setEditingPort(port);
    setFormName(port.name);
    setFormCountryId(port.country_id ? String(port.country_id) : '');
    setFormError('');
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingPort(null);
    setFormName('');
    setFormCountryId('');
    setFormError('');
  };

  const handleSave = async () => {
    const trimmed = formName.trim();
    if (!trimmed) { setFormError('El nombre es obligatorio.'); return; }
    if (!formCountryId) { setFormError('El país es obligatorio.'); return; }
    setSaving(true);
    setFormError('');
    try {
      const payload = { name: trimmed, country_id: parseInt(formCountryId, 10) };
      if (editingPort) {
        await api.put(`${ENDPOINT}${editingPort.id}/`, payload);
      } else {
        await api.post(ENDPOINT, payload);
      }
      cancelForm();
      load();
    } catch (err) {
      setFormError(parseErrorMessage(err) || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteError('');
    try {
      await api.delete(`${ENDPOINT}${id}/`);
      setDeleteId(null);
      load();
    } catch (err) {
      setDeleteError(parseErrorMessage(err) || 'No se pudo eliminar.');
    }
  };

  const q = searchTerm.toLowerCase();
  const filtered = ports.filter((p) =>
    p.name.toLowerCase().includes(q) ||
    (p.country_name || '').toLowerCase().includes(q)
  );

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Anchor className="w-5 h-5 text-blue-600" />
          <h2 className="text-base font-semibold text-gray-800">Puertos</h2>
          <span className="text-xs text-gray-400 font-normal">(Fábrica y Embarque)</span>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" /> Nuevo puerto
        </button>
      </div>

      {showForm && (
        <div className="px-5 py-4 bg-blue-50 border-b border-blue-100">
          <p className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">
            {editingPort ? 'Editar puerto' : 'Nuevo puerto'}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 items-start">
            <div className="flex-1 w-full">
              <label className="block text-xs font-medium text-gray-600 mb-1">Nombre</label>
              <input
                autoFocus
                type="text"
                value={formName}
                onChange={(e) => { setFormName(e.target.value); setFormError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') cancelForm(); }}
                placeholder="Ej: Shanghai, Ningbo, Buenos Aires…"
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  formError && !formName.trim() ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
                }`}
              />
            </div>
            <div className="sm:w-56 w-full">
              <label className="block text-xs font-medium text-gray-600 mb-1">País</label>
              <select
                value={formCountryId}
                onChange={(e) => { setFormCountryId(e.target.value); setFormError(''); }}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white ${
                  formError && !formCountryId ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar país</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 pt-5">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg flex items-center gap-1 transition-colors"
              >
                <Check className="w-4 h-4" /> {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                onClick={cancelForm}
                className="px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 text-sm font-medium rounded-lg flex items-center gap-1 transition-colors"
              >
                <X className="w-4 h-4" /> Cancelar
              </button>
            </div>
          </div>
          {formError && (
            <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {formError}
            </p>
          )}
        </div>
      )}

      <div className="px-5 py-3 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar puerto o país…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 bg-gray-50"
          />
        </div>
      </div>

      <div className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">
            {searchTerm ? 'Sin resultados para esa búsqueda.' : 'No hay puertos cargados. Hacé clic en "Nuevo puerto" para agregar.'}
          </div>
        ) : (
          filtered.map((port) => (
            <div
              key={port.id}
              className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 group transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Anchor className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-400 transition-colors shrink-0" />
                <span className="text-sm text-gray-800 truncate">{port.name}</span>
                {port.country_name && (
                  <span className="text-xs text-gray-400 shrink-0">{port.country_name}</span>
                )}
              </div>

              {deleteId === port.id ? (
                <div className="flex items-center gap-2">
                  {deleteError && (
                    <span className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {deleteError}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">¿Eliminar?</span>
                  <button
                    onClick={() => handleDelete(port.id)}
                    className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-md flex items-center gap-1 transition-colors"
                  >
                    <Check className="w-3 h-3" /> Sí
                  </button>
                  <button
                    onClick={() => { setDeleteId(null); setDeleteError(''); }}
                    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded-md flex items-center gap-1 transition-colors"
                  >
                    <X className="w-3 h-3" /> No
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEdit(port)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => { setDeleteId(port.id); setDeleteError(''); }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {filtered.length > 0 && (
        <div className="px-5 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400 text-right">
          {filtered.length} puerto{filtered.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
